import type { Principal, UUID } from "./sync/types";

// mentionQuery is the "@..." being typed just before the caret, or null.
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const m = /(^|\s)@([a-z0-9._-]*)$/i.exec(text.slice(0, caret));
  if (!m) return null;
  return { start: caret - m[2].length - 1, query: m[2].toLowerCase() };
}

// emojiQuery is the ":..." being typed just before the caret, once it has
// two letters, as Slack offers emoji, or null.
export function emojiQuery(text: string, caret: number): { start: number; query: string } | null {
  const m = /(^|\s):([a-z0-9_+-]{2,})$/i.exec(text.slice(0, caret));
  if (!m) return null;
  return { start: caret - m[2].length - 1, query: m[2].toLowerCase() };
}

// mentionCandidates are the people and agents whose handle, or a word of
// whose name, starts with the query: the channel's members first, then
// handle matches before name matches, alphabetically.
export function mentionCandidates(people: Iterable<Principal>, members: Set<UUID>, me: UUID | undefined, query: string, limit = 6) {
  const scored: { p: Principal; score: number }[] = [];
  for (const p of people) {
    if (p.id === me || p.status !== "active" || p.kind === "system") continue;
    const handle = p.handle.toLowerCase();
    const words = (p.display_name ?? "").toLowerCase().split(/\s+/);
    let score: number;
    if (handle.startsWith(query)) score = 0;
    else if (words.some((w) => w.startsWith(query))) score = 1;
    else continue;
    if (!members.has(p.id)) score += 10;
    scored.push({ p, score });
  }
  scored.sort((a, b) => a.score - b.score || a.p.handle.localeCompare(b.p.handle));
  return scored.slice(0, limit).map((s) => ({ ...s.p, member: s.score < 10 }));
}

// groupCandidates are the user groups whose handle, or a word of whose
// name, starts with the query.
export function groupCandidates<G extends { handle: string; name: string }>(groups: Iterable<G>, query: string, limit = 3): G[] {
  const out: { g: G; score: number }[] = [];
  for (const g of groups) {
    if (g.handle.startsWith(query)) out.push({ g, score: 0 });
    else if (g.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(query))) out.push({ g, score: 1 });
  }
  out.sort((a, b) => a.score - b.score || a.g.handle.localeCompare(b.g.handle));
  return out.slice(0, limit).map((x) => x.g);
}
